import { ListItem } from '@apollo/space-kit/ListItem';
import { Select } from '@apollo/space-kit/Select';
import classnames from 'classnames';
import { keyBy } from 'lodash';
import React, { useMemo } from 'react';

import {
  ImplementingService,
  getGraphMetaData,
} from '../explorerPage/helpers/federationHelpers';

export const SubgraphSelect = ({
  triggerText,
  selectedSubgraphName,
  setSelectedSubgraphName,
  disableTrigger = false,
  subgraphs,
}: {
  triggerText: string;
  selectedSubgraphName: string | null;
  setSelectedSubgraphName: (subgraphId: string) => void;
  disableTrigger?: boolean;
  subgraphs: ImplementingService[] | undefined;
}) => {
  const keyedSubgraphs = useMemo(
    () => keyBy(subgraphs, (subgraph) => subgraph.name),
    [subgraphs],
  );

  return (
    <Select
      disabled={disableTrigger}
      feel="flat"
      size="standard"
      className="max-w-44 truncate"
      listAs={<div className="max-h-72 w-72 outline-none" />}
      onChange={(e) => setSelectedSubgraphName(e.target.value)}
      renderTriggerNode={() => <>{triggerText ?? 'select a subgraph'}</>}
      renderListItem={(itemProps, options) => {
        const subgraphName = options.props.value;
        const subgraph =
          typeof subgraphName === 'string' && keyedSubgraphs[subgraphName];
        if (!subgraph) {
          throw Error(
            `Could not find subgraph: ${subgraphName} in the subgraphs.`,
          );
        }

        const contactDirectiveMetaData = getGraphMetaData(subgraph?.typeDefs);

        return (
          <ListItem
            {...itemProps}
            key={subgraphName}
            className="size-full rounded p-1 px-2 text-base"
          >
            <div className="text-left">
              <div className="relative truncate">
                <span className="font-semibold">{subgraphName}</span>
              </div>
              {contactDirectiveMetaData.description && (
                <div
                  className={classnames(
                    'truncate text-sm',
                    itemProps.selected ? 'text-inverted' : 'text-disabled',
                  )}
                >
                  {contactDirectiveMetaData.description}
                </div>
              )}
            </div>
          </ListItem>
        );
      }}
      value={selectedSubgraphName}
    >
      {Object.values(keyedSubgraphs)
        .sort((subgraphA, subgraphB) =>
          subgraphA.name > subgraphB.name ? 1 : -1,
        )
        .map((subgraph) => {
          return <option value={subgraph.name} key={subgraph.name} />;
        })}
    </Select>
  );
};
